import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@klear/ui/lib/utils'

export function useScrolledOut(offset = 64): [(node: Element | null) => void, boolean] {
  const [node, setNode] = useState<Element | null>(null)
  const [out, setOut] = useState(false)
  useEffect(() => {
    const root = window.document.querySelector('main')
    if (node == null || root == null) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry == null) return
        const top = (entry.rootBounds?.top ?? 0) + offset
        setOut(!entry.isIntersecting && entry.boundingClientRect.top < top)
      },
      { root, rootMargin: `-${offset}px 0px 0px 0px` }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, offset])
  return [setNode, out]
}

export function FloatingHeader({ visible, children }: { visible: boolean; children: ReactNode }) {
  const bar = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (bar.current != null) bar.current.inert = !visible
  }, [visible])
  return (
    <div className="sticky top-0 z-20 -mx-8 -mt-8 mb-8 h-0">
      <div
        ref={bar}
        aria-hidden={!visible}
        className={cn(
          'flex flex-wrap items-center gap-4 border-b bg-background/95 px-8 py-3 backdrop-blur transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
          visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function useScrollMainToTop(key: unknown): void {
  useEffect(() => {
    const main = window.document.querySelector('main')
    if (main != null) main.scrollTop = 0
  }, [key])
}
